import { DOCUMENT } from '@angular/common';
import { CanMatchFn, Route, Router, UrlSegment, UrlTree } from '@angular/router';
import { inject } from '@angular/core';
import { APP_CONFIG } from '@/app/core/config/app-config.token';
import { APP_NAV_SECTIONS } from '@/app/shared/nav/nav-sections.config';
import { NavItem } from '@/app/shared/nav/nav-section.model';
import { IDENTIDAD_STORAGE_KEY, IDENTIDADES_SINTETICAS } from '@/app/features/catalogos-comerciales/models/identidades-sinteticas';

export type PerfilRuta = 'administrador' | 'agente_ventas' | 'jefe_comercial' | 'director' | 'soporte';
export type EstadoSesion = 'activa' | 'sin_sesion' | 'mfa_pendiente';
export type MotivoAccesoNegado = 'sin_sesion' | 'sin_permiso' | 'mfa_pendiente';
export type MotivoAcceso = MotivoAccesoNegado | 'sin_gestor';
export type ResultadoAcceso = { permitido: true } | { permitido: false; motivo: MotivoAccesoNegado };
export interface IdentidadResuelta {
  corporateIdentityId: string;
  perfil: PerfilRuta;
  mfaVerificado: boolean;
}

export const PERFILES_CON_MFA_OBLIGATORIO: readonly PerfilRuta[] = ['administrador', 'soporte'];

/**
 * FE-82 defecto 1: la ficha (`/prospectos/:prospectoId`) es la única pantalla de "Mi cartera"
 * que además del agente titular abre, en solo lectura, al administrador (BE-49a). La lista
 * ('/prospectos') y el resto de hijos de esa ruta siguen exclusivos de agente_ventas.
 */
export const PERFILES_FICHA_PROSPECTO: readonly PerfilRuta[] = ['agente_ventas', 'administrador'];

interface StoredIdentity {
  corporateIdentityId?: string;
  mfaVerificado?: unknown;
}

function flattenNavItems(items: NavItem[]): NavItem[] {
  return items.flatMap((item) => [item, ...(item.children ? flattenNavItems(item.children) : [])]);
}

function uniqueProfiles(profiles: string[]): PerfilRuta[] {
  return Array.from(new Set(profiles)) as PerfilRuta[];
}

export function isProfileAllowed(profile: PerfilRuta, allowedProfiles: readonly string[] | null): boolean {
  return !allowedProfiles?.length || allowedProfiles.includes(profile);
}

export function resolveAllowedProfilesFromMenu(routePrefix: string): PerfilRuta[] | null {
  const navItems = flattenNavItems(APP_NAV_SECTIONS.flatMap((section) => section.items)).filter(
    (item) => typeof item.route === 'string',
  );
  const exactMatch = navItems.find((item) => item.route === routePrefix);

  if (exactMatch?.profiles?.length) {
    return uniqueProfiles(exactMatch.profiles);
  }

  const descendantProfiles = navItems
    .filter((item) => item.route?.startsWith(`${routePrefix}/`))
    .flatMap((item) => item.profiles ?? []);

  return descendantProfiles.length > 0 ? uniqueProfiles(descendantProfiles) : null;
}

export function resolveSelectedIdentity(storage: Storage | null | undefined): IdentidadResuelta | null {
  const raw = storage?.getItem(IDENTIDAD_STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as StoredIdentity;
    const identity = IDENTIDADES_SINTETICAS.find(
      (candidate) => candidate.corporateIdentityId === parsed.corporateIdentityId,
    );

    if (!identity) {
      return null;
    }

    return {
      corporateIdentityId: identity.corporateIdentityId,
      perfil: identity.perfilEsperado as PerfilRuta,
      mfaVerificado: parsed.mfaVerificado === true,
    };
  } catch {
    return null;
  }
}

export function resolveSelectedProfile(storage: Storage | null | undefined): PerfilRuta | null {
  return resolveSelectedIdentity(storage)?.perfil ?? null;
}

export function esModoIdentidadLocal(config: { environment: string } | null | undefined): boolean {
  return config?.environment === 'dev';
}

export function resolveSessionState(storage: Storage | null | undefined, modoIdentidadLocal: boolean): EstadoSesion {
  if (!modoIdentidadLocal) {
    return 'activa';
  }

  const identidad = resolveSelectedIdentity(storage);
  if (!identidad) {
    return 'sin_sesion';
  }

  if (PERFILES_CON_MFA_OBLIGATORIO.includes(identidad.perfil) && identidad.mfaVerificado !== true) {
    return 'mfa_pendiente';
  }

  return 'activa';
}

export function buildRestrictedAccessRedirect(router: Router): UrlTree {
  return router.createUrlTree(['/acceso-restringido']);
}

export function canProfileOpenRoute(ruta: string, storage: Storage | null | undefined): boolean {
  const selectedProfile = resolveSelectedProfile(storage);
  if (!selectedProfile) {
    return true;
  }

  return isProfileAllowed(selectedProfile, resolveAllowedProfilesFromMenu(ruta));
}

export function evaluateRouteAccess(
  menuAccess: string,
  perfilesPermitidos: readonly string[] | null | undefined,
  storage: Storage | null | undefined,
  modoIdentidadLocal: boolean,
): ResultadoAcceso {
  const identidad = resolveSelectedIdentity(storage);
  if (!identidad) {
    return modoIdentidadLocal ? { permitido: false, motivo: 'sin_sesion' } : { permitido: true };
  }

  const allowedProfiles = perfilesPermitidos ?? resolveAllowedProfilesFromMenu(menuAccess);
  if (!isProfileAllowed(identidad.perfil, allowedProfiles)) {
    return { permitido: false, motivo: 'sin_permiso' };
  }

  if (resolveSessionState(storage, modoIdentidadLocal) === 'mfa_pendiente') {
    return { permitido: false, motivo: 'mfa_pendiente' };
  }

  return { permitido: true };
}

export function buildAccessDeniedRedirect(router: Router, motivo: MotivoAccesoNegado): UrlTree {
  const path =
    motivo === 'sin_sesion' ? '/sin-sesion' : motivo === 'mfa_pendiente' ? '/mfa-requerido' : '/acceso-restringido';
  return router.createUrlTree([path]);
}

export const routeProfileCanMatch: CanMatchFn = (route: Route) => {
  const router = inject(Router);
  const document = inject(DOCUMENT);
  const config = inject(APP_CONFIG, { optional: true });
  const menuAccess = route.data?.['menuAccess'];

  if (typeof menuAccess !== 'string' || !menuAccess.trim()) {
    return true;
  }

  const perfilesRuta = route.data?.['perfilesPermitidos'];
  const allowedProfiles = Array.isArray(perfilesRuta) ? (perfilesRuta as string[]) : null;

  const resultado = evaluateRouteAccess(
    menuAccess,
    allowedProfiles,
    document.defaultView?.sessionStorage,
    esModoIdentidadLocal(config),
  );

  return resultado.permitido ? true : buildAccessDeniedRedirect(router, resultado.motivo);
};

/**
 * FE-82 defecto 1: guarda de la ficha del prospecto. `prospectos/:prospectoId` es una ruta
 * hermana de `prospectos` (no un hijo) para que un perfil fuera de agente_ventas pueda
 * entrar a la ficha aunque la lista lo tenga bloqueado: `routeProfileCanMatch` redirige con un
 * UrlTree en cuanto lo evalúa, y Angular ya no intenta la siguiente ruta candidata. Antes de
 * delegar, descarta los dos segundos segmentos literales que ya tiene el padre ('nuevo',
 * 'agenda') devolviendo `false` (no un UrlTree): eso sí deja que el router siga probando la
 * ruta de `prospectos` con sus hijos, como corresponde a esas dos pantallas.
 */
export const fichaProspectoCanMatch: CanMatchFn = (route: Route, segments: UrlSegment[]) => {
  const idSegment = segments[1]?.path;
  if (idSegment === 'nuevo' || idSegment === 'agenda') {
    return false;
  }

  return routeProfileCanMatch(route, segments);
};

export function pendingAccessCanMatch(motivo: 'sin_sesion' | 'mfa_pendiente'): CanMatchFn {
  return () => {
    const router = inject(Router);
    const document = inject(DOCUMENT);
    const config = inject(APP_CONFIG, { optional: true });
    const estado = resolveSessionState(document.defaultView?.sessionStorage, esModoIdentidadLocal(config));

    return estado === motivo ? true : router.createUrlTree(['/']);
  };
}
